// src/game/types.ts
// Shared leaf types & direction/side constants (LOCKED contract §3).
// No module hardcodes a display name; this file owns the sign conventions (§2).

// ---- Color (§5.2 palette; modules use only semantic ids, never hex) --------
export type Color = 'red' | 'blue' | 'green' | 'yellow' | 'purple' | 'orange';

/** Canonical color order. Difficulty selects a shuffled prefix. */
export const COLORS: readonly Color[] = ['red', 'blue', 'green', 'yellow', 'purple', 'orange'];

// ---- Direction -------------------------------------------------------------
export type Direction = 'up' | 'down' | 'left' | 'right';

/** Canonical direction order (used by legalMoves tie-breaking). */
export const DIRECTIONS: readonly Direction[] = ['up', 'down', 'left', 'right'];

export interface Delta {
  readonly drow: number;
  readonly dcol: number;
}

/**
 * The ONLY legal sign convention (§2). Keyed access DELTAS[dir] is `Delta`
 * (NOT `Delta | undefined`) under noUncheckedIndexedAccess because the key type
 * is exactly `Direction`. Down & right are positive.
 */
export const DELTAS: Readonly<Record<Direction, Delta>> = {
  up: { drow: -1, dcol: 0 },
  down: { drow: +1, dcol: 0 },
  left: { drow: 0, dcol: -1 },
  right: { drow: 0, dcol: +1 },
};

/** up<->down, left<->right. */
export function opposite(dir: Direction): Direction {
  switch (dir) {
    case 'up':
      return 'down';
    case 'down':
      return 'up';
    case 'left':
      return 'right';
    case 'right':
      return 'left';
    default: {
      const _exhaustive: never = dir;
      return _exhaustive;
    }
  }
}

// ---- Sides / gate facing ---------------------------------------------------
export type Side = 'top' | 'bottom' | 'left' | 'right';

/** A block exits a `side` gate by traveling in this direction. Frozen. */
export const SIDE_EXIT_DIRECTION: Readonly<Record<Side, Direction>> = {
  top: 'up',
  bottom: 'down',
  left: 'left',
  right: 'right',
};

// ---- Coordinates -----------------------------------------------------------
export interface Cell {
  readonly row: number;
  readonly col: number;
}

export function cellEquals(a: Cell, b: Cell): boolean {
  return a.row === b.row && a.col === b.col;
}

// ---- Ids -------------------------------------------------------------------
/**
 * Plain number. INVARIANT: BlockId >= 0, unique within a GameState, assigned
 * 0,1,2,… in spawn order, stable across slides, gone after exit.
 */
export type BlockId = number;

// ---- Move (the one verb, §2.3) ---------------------------------------------
/**
 * A player/solver input: which block, which direction, and HOW FAR (free-move slide).
 * `distance` is the number of cells to travel (>= 1). A slide is clamped at the last fully
 * clear cell, so an over-large distance simply rests against the wall/block. EXIT is still
 * emergent (never encoded as its own kind): an exit move is encoded as a slide with a distance
 * large enough to carry the piece off-board through its matching gate — `legalMoves` uses
 * EXIT_DISTANCE (= board.rows + board.cols) for that, see rules.ts.
 */
export interface Move {
  readonly blockId: BlockId;
  readonly dir: Direction;
  readonly distance: number;
}
